import { useEffect, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { type ApiError } from '@/lib/api/client';
import { fmtMoney } from '@/lib/domain/format';
import { useMe } from '@/features/identity/useMe';
import { useToast } from '@/components/ui/toast';
import { LEGAL_STATUSES, legalStatusValue } from '@/lib/domain/legalStatus';
import { type ApiDealResponse, readApiETag } from './types';
import { useReferenceData } from './useReferenceData';
import { type ComboItem, EntityCombobox } from './EntityCombobox';
import { useCompanies, useOriginators, usePeople } from './useEntities';
import { CompanyInline, OriginatorInline, PersonInline } from './InlineEntityForms';
import { type UpdateDealBody, useUpdateDeal } from './useUpdateDeal';

const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink outline-none';
const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

/**
 * Modo de edição da ficha (E6 + lote 1). Edita os campos escalares e os vínculos do deal:
 * **Empresa-alvo** (opcional — item 1), **Originadora** (item 8) e **Pessoa originadora** (follow-up)
 * são editáveis via EntityCombobox "buscar ou criar nova" (mini-forms compartilhados). A originadora
 * externa respeita a originação interna (deal interno → snapshot externo = null, combobox desabilitado,
 * mesmo padrão do NewDealModal). Salva via UpdateDeal + If-Match (concorrência otimista; 412 no hook).
 */
export function DealEditForm({
  deal,
  onDone,
  onCancel,
}: {
  deal: ApiDealResponse;
  onDone: () => void;
  onCancel: () => void;
}) {
  const reference = useReferenceData();
  const companies = useCompanies(true);
  const originators = useOriginators(true);
  const people = usePeople(true);
  const update = useUpdateDeal();
  const toast = useToast();
  // Item 6: editar a entidade vinculada (Pessoa/Originadora/Empresa) na ficha — só admin.
  const me = useMe(true);
  const isAdmin = me.data?.isAdmin ?? false;

  // Vínculos editáveis. Deal de originação interna não tem originadora externa.
  const isInternalOrigination = deal.internalOriginatorId != null;
  const [company, setCompany] = useState<ComboItem | null>(null);
  const [externalOriginator, setExternalOriginator] = useState<ComboItem | null>(null);
  const [person, setPerson] = useState<ComboItem | null>(null);
  const [refsInitialized, setRefsInitialized] = useState(false);
  const [inlineKind, setInlineKind] = useState<null | 'company' | 'originator' | 'person'>(null);
  const [inlineDraft, setInlineDraft] = useState('');
  // Modo edição (Item 6): prefill da entidade selecionada. null = criar.
  const [inlineEdit, setInlineEdit] = useState<
    null | { id: string; name: string; sectorId?: number; typeId?: number; email?: string | null }
  >(null);

  // A ficha guarda só os ids dos vínculos; resolve os nomes pelas listas carregadas (uma vez).
  useEffect(() => {
    if (refsInitialized || !companies.data || !originators.data || !people.data) return;
    if (deal.targetCompanyId) {
      const c = companies.data.items.find((x) => x.id === deal.targetCompanyId);
      setCompany(c ? { id: c.id, name: c.name } : { id: deal.targetCompanyId, name: 'Empresa atual' });
    }
    if (deal.originatorIdSnapshot) {
      const o = originators.data.items.find((x) => x.id === deal.originatorIdSnapshot);
      setExternalOriginator(o ? { id: o.id, name: o.name } : { id: deal.originatorIdSnapshot, name: 'Originadora atual' });
    }
    if (deal.originatorPersonIdSnapshot) {
      const p = people.data.items.find((x) => x.id === deal.originatorPersonIdSnapshot);
      setPerson(p ? { id: p.id, name: p.name } : { id: deal.originatorPersonIdSnapshot, name: 'Pessoa atual' });
    }
    setRefsInitialized(true);
  }, [companies.data, originators.data, people.data, deal, refsInitialized]);

  const [codename, setCodename] = useState(deal.codename ?? '');
  const [legalStatus, setLegalStatus] = useState<number>(legalStatusValue(deal.legalStatus));
  const [thesisIds, setThesisIds] = useState<number[]>(deal.thesisIds ?? []);
  const [thesisSummary, setThesisSummary] = useState(deal.thesisSummary ?? '');
  const [tags, setTags] = useState<string[]>(deal.tags ?? []);
  const [tagDraft, setTagDraft] = useState('');
  // Split do cheque (Item 3): disc/coinvest editáveis, total = soma (read-only). Deals legados (só
  // total, sem split) abrem com disc = total e coinvest = 0 — espelha o backfill da migration.
  const [disc, setDisc] = useState(
    deal.checkDiscretionaryBRL != null
      ? String(deal.checkDiscretionaryBRL)
      : deal.investmentValueBRL != null
        ? String(deal.investmentValueBRL)
        : '',
  );
  const [coinvest, setCoinvest] = useState(
    deal.checkCoinvestmentBRL != null
      ? String(deal.checkCoinvestmentBRL)
      : deal.investmentValueBRL != null
        ? '0'
        : '',
  );
  const [leadUserId, setLeadUserId] = useState(deal.leadUserId);
  const [teamUserIds, setTeamUserIds] = useState<string[]>(deal.teamUserIds ?? []);

  const codenameOk = codename.trim().length > 0;
  const canSave = codenameOk && !update.isPending;

  // Total do cheque = discricionário + coinvestimento (read-only). Ambos vazios → sem cheque (null).
  const discNum = disc.replace(/\D/g, '') ? Number(disc.replace(/\D/g, '')) : null;
  const coinvestNum = coinvest.replace(/\D/g, '') ? Number(coinvest.replace(/\D/g, '')) : null;
  const hasCheck = discNum != null || coinvestNum != null;
  const totalCheck = hasCheck ? (discNum ?? 0) + (coinvestNum ?? 0) : null;

  const companyItems = (companies.data?.items ?? []).map<ComboItem>((c) => ({
    id: c.id,
    name: c.name,
    subtitle: reference.data?.sectors.find((s) => s.id === c.sectorId)?.name,
  }));
  const originatorItems = (originators.data?.items ?? []).map<ComboItem>((o) => ({
    id: o.id,
    name: o.name,
    subtitle: reference.data?.originatorTypes.find((t) => t.id === o.typeId)?.name,
  }));
  // Item 2: Pessoa pertence a uma única Originadora — picker filtra pela originadora selecionada e
  // fica desabilitado sem ela.
  const selectedOriginatorId = externalOriginator?.id ?? null;
  const personItems = (people.data?.items ?? [])
    .filter((p) => selectedOriginatorId != null && p.currentOriginatorId === selectedOriginatorId)
    .map<ComboItem>((p) => ({
      id: p.id,
      name: p.name,
      subtitle: p.email ?? undefined,
    }));

  // Ao trocar a originadora, limpa a pessoa se ela não pertencer à nova (avisa discretamente).
  const clearPersonIfMismatch = (newOriginatorId: string | null) => {
    if (!person) return;
    const personOrig = people.data?.items.find((p) => p.id === person.id)?.currentOriginatorId ?? null;
    if (personOrig !== newOriginatorId) {
      setPerson(null);
      toast('Pessoa removida — não pertence à originadora selecionada.', 'warn');
    }
  };

  const onInlineCreated = (item: ComboItem) => {
    if (inlineKind === 'company') setCompany(item);
    if (inlineKind === 'originator') setExternalOriginator(item);
    if (inlineKind === 'person') setPerson(item);
    setInlineKind(null);
    setInlineDraft('');
    setInlineEdit(null);
  };

  // Abre o mini-form em modo edição, pré-preenchido pela list-item da entidade selecionada (Item 6).
  const openEdit = (kind: 'company' | 'originator' | 'person') => {
    if (kind === 'company' && company) {
      const c = companies.data?.items.find((x) => x.id === company.id);
      if (c) setInlineEdit({ id: c.id, name: c.name, sectorId: c.sectorId });
    } else if (kind === 'originator' && externalOriginator) {
      const o = originators.data?.items.find((x) => x.id === externalOriginator.id);
      if (o) setInlineEdit({ id: o.id, name: o.name, typeId: o.typeId });
    } else if (kind === 'person' && person) {
      const p = people.data?.items.find((x) => x.id === person.id);
      if (p) setInlineEdit({ id: p.id, name: p.name, email: p.email });
    } else {
      return;
    }
    setInlineKind(kind);
  };

  const toggleThesis = (id: number) =>
    setThesisIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleTeam = (id: string) =>
    setTeamUserIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const addTag = () => {
    const t = tagDraft.trim();
    if (t && t.length <= 50 && !tags.includes(t)) setTags([...tags, t]);
    setTagDraft('');
  };

  const onSave = () => {
    if (!canSave) return;
    const body: UpdateDealBody = {
      codename: codename.trim(),
      legalStatus,
      folderId: deal.folderId,
      targetCompanyId: company?.id ?? null,
      originatorIdSnapshot: isInternalOrigination ? null : (externalOriginator?.id ?? null),
      originatorPersonIdSnapshot: person?.id ?? null,
      internalOriginatorId: deal.internalOriginatorId,
      leadUserId,
      // Total = disc + coinvest (validado no backend). Sem partes → tudo null.
      investmentValueBRL: totalCheck,
      checkDiscretionaryBRL: hasCheck ? (discNum ?? 0) : null,
      checkCoinvestmentBRL: hasCheck ? (coinvestNum ?? 0) : null,
      thesisSummary: thesisSummary.trim() || null,
      isPriority: deal.isPriority,
      thesisIds,
      teamUserIds,
      tags,
    };
    update.mutate(
      { id: deal.id, etag: readApiETag(deal), body },
      {
        onSuccess: onDone,
        // Achado F1 (revisão 2026-07): no 412, o formulário continuava montado com os valores
        // antigos, mas o refetch do detalhe (feito pelo hook) atualizava o ETag da prop `deal` — um
        // 2º clique em "Salvar" enviaria o ETag NOVO com os dados VELHOS, passando no If-Match e
        // sobrescrevendo silenciosamente a edição de quem alterou no meio. Fechar o form no 412
        // fecha essa janela: para editar de novo, o usuário reabre com o dado já atualizado.
        onError: (err) => {
          if ((err as ApiError).status === 412) onDone();
        },
      },
    );
  };

  return (
    <>
    <div className="px-6 py-5">
      <div className="mb-4">
        <span className={LABEL}>Codinome *</span>
        <input
          value={codename}
          onChange={(e) => setCodename(e.target.value)}
          maxLength={200}
          autoFocus
          className={INPUT}
        />
        {!codenameOk && (
          <span className="mt-1 block text-[11px] text-vega-danger">Codinome é obrigatório.</span>
        )}
      </div>

      <div className="mb-4">
        <span className={LABEL}>Status processual</span>
        <select
          value={legalStatus}
          onChange={(e) => setLegalStatus(Number(e.target.value))}
          className={INPUT}
        >
          {LEGAL_STATUSES.map((s) => (
            <option key={s.apiValue} value={s.apiValue}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <span className={LABEL}>Tese</span>
        {reference.isLoading ? (
          <span className="text-[12px] text-vega-muted-2">Carregando teses…</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {reference.data?.theses.map((t) => {
              const active = thesisIds.includes(t.id);
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => toggleThesis(t.id)}
                  className={`rounded px-2.5 py-1 text-[11.5px] font-medium ${
                    active ? 'bg-vega-ink text-vega-bg' : 'bg-vega-bg-2 text-vega-ink-2'
                  }`}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-4">
        <span className={LABEL}>Tags</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded bg-vega-bg-2 px-2 py-1 text-[11.5px] text-vega-ink-2"
            >
              {t}
              <button
                type="button"
                aria-label={`Remover tag ${t}`}
                onClick={() => setTags(tags.filter((x) => x !== t))}
                className="text-vega-muted hover:text-vega-danger"
              >
                <X size={11} />
              </button>
            </span>
          ))}
          <input
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            onBlur={addTag}
            placeholder="+ tag"
            maxLength={50}
            className="w-24 rounded border border-vega-line bg-vega-surface px-2 py-1 text-[11.5px] text-vega-ink outline-none"
          />
        </div>
      </div>

      <div className="mb-4">
        <span className={LABEL}>Resumo da tese</span>
        <textarea
          value={thesisSummary}
          onChange={(e) => setThesisSummary(e.target.value)}
          rows={4}
          maxLength={4000}
          className={`${INPUT} resize-y leading-relaxed`}
        />
      </div>

      <div className="mb-4">
        <span className={LABEL}>Responsável pelo deal</span>
        <select
          value={leadUserId}
          onChange={(e) => setLeadUserId(e.target.value)}
          className={INPUT}
        >
          {reference.data?.leadUsers.some((u) => u.id === leadUserId) ? null : (
            <option value={leadUserId}>(responsável atual)</option>
          )}
          {reference.data?.leadUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <EntityCombobox
          label="Empresa-alvo"
          items={companyItems}
          loading={companies.isLoading}
          fetching={companies.isFetching}
          value={company}
          onSelect={setCompany}
          onCreateNew={(draft) => {
            setInlineDraft(draft);
            setInlineEdit(null);
            setInlineKind('company');
          }}
        />
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[11px] text-vega-muted-2">Opcional — deixe vazio se ainda não definida.</span>
          {isAdmin && company && (
            <button
              type="button"
              onClick={() => openEdit('company')}
              className="inline-flex items-center gap-1 text-[11px] text-vega-muted hover:text-vega-ink"
            >
              <Pencil size={11} /> Editar empresa
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <EntityCombobox
          label="Originadora"
          items={originatorItems}
          loading={originators.isLoading}
          fetching={originators.isFetching}
          value={externalOriginator}
          disabled={isInternalOrigination}
          onSelect={(item) => {
            setExternalOriginator(item);
            clearPersonIfMismatch(item?.id ?? null);
          }}
          onCreateNew={(draft) => {
            setInlineDraft(draft);
            setInlineEdit(null);
            setInlineKind('originator');
          }}
        />
        <div className="mt-1 flex items-center justify-between">
          {isInternalOrigination ? (
            <span className="text-[11px] text-vega-muted-2">Originação interna — sem originadora externa.</span>
          ) : (
            <span />
          )}
          {isAdmin && externalOriginator && (
            <button
              type="button"
              onClick={() => openEdit('originator')}
              className="inline-flex items-center gap-1 text-[11px] text-vega-muted hover:text-vega-ink"
            >
              <Pencil size={11} /> Editar originadora
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <EntityCombobox
          label="Pessoa originadora"
          items={personItems}
          loading={people.isLoading}
          fetching={people.isFetching}
          value={person}
          disabled={!selectedOriginatorId}
          onSelect={setPerson}
          onCreateNew={(draft) => {
            setInlineDraft(draft);
            setInlineEdit(null);
            setInlineKind('person');
          }}
        />
        <div className="mt-1 flex items-center justify-between">
          {!selectedOriginatorId ? (
            <span className="text-[11px] text-vega-muted-2">Selecione a originadora primeiro.</span>
          ) : (
            <span />
          )}
          {isAdmin && person && (
            <button
              type="button"
              onClick={() => openEdit('person')}
              className="inline-flex items-center gap-1 text-[11px] text-vega-muted hover:text-vega-ink"
            >
              <Pencil size={11} /> Editar pessoa
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <span className={LABEL}>Equipe</span>
        {reference.isLoading ? (
          <span className="text-[12px] text-vega-muted-2">Carregando usuários…</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {reference.data?.users.map((u) => {
              const active = teamUserIds.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => toggleTeam(u.id)}
                  className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${
                    active ? 'bg-vega-accent-soft text-vega-accent' : 'bg-vega-bg-2 text-vega-ink-2'
                  }`}
                >
                  {u.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-5">
        <span className={LABEL}>Cheque (R$)</span>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1 block text-[10.5px] text-vega-muted-2">Discricionário</span>
            <input
              value={disc}
              onChange={(e) => setDisc(e.target.value)}
              inputMode="numeric"
              placeholder="0"
              className={`${INPUT} font-mono`}
            />
          </div>
          <div>
            <span className="mb-1 block text-[10.5px] text-vega-muted-2">Coinvestimento</span>
            <input
              value={coinvest}
              onChange={(e) => setCoinvest(e.target.value)}
              inputMode="numeric"
              placeholder="0"
              className={`${INPUT} font-mono`}
            />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between rounded bg-vega-bg-2 px-2.5 py-2">
          <span className="text-[11px] uppercase tracking-wide text-vega-muted">Cheque total</span>
          <span className="font-mono text-[13px] font-semibold text-vega-ink">{fmtMoney(totalCheck ?? 0)}</span>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-vega-line pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={update.isPending}
          className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
        >
          {update.isPending ? 'Salvando…' : 'Salvar alterações'}
        </button>
      </div>
    </div>

      {inlineKind === 'company' && (
        <CompanyInline
          draft={inlineDraft}
          edit={
            inlineEdit && inlineEdit.sectorId != null
              ? { id: inlineEdit.id, name: inlineEdit.name, sectorId: inlineEdit.sectorId }
              : undefined
          }
          onCreated={onInlineCreated}
          onCancel={() => {
            setInlineKind(null);
            setInlineEdit(null);
          }}
        />
      )}
      {inlineKind === 'originator' && (
        <OriginatorInline
          draft={inlineDraft}
          edit={
            inlineEdit && inlineEdit.typeId != null
              ? { id: inlineEdit.id, name: inlineEdit.name, typeId: inlineEdit.typeId }
              : undefined
          }
          onCreated={onInlineCreated}
          onCancel={() => {
            setInlineKind(null);
            setInlineEdit(null);
          }}
        />
      )}
      {inlineKind === 'person' && (
        <PersonInline
          draft={inlineDraft}
          originatorId={externalOriginator?.id ?? null}
          originatorName={externalOriginator?.name ?? null}
          edit={
            inlineEdit ? { id: inlineEdit.id, name: inlineEdit.name, email: inlineEdit.email ?? null } : undefined
          }
          onCreated={onInlineCreated}
          onCancel={() => {
            setInlineKind(null);
            setInlineEdit(null);
          }}
        />
      )}
    </>
  );
}
